import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Zap, X, CreditCard, Check, Loader2, Minus, Plus } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useQuota } from '../../contexts/QuotaContext';
import { AnimatedIconButton } from './AnimatedIconButton';
import { getTopupInfo, purchaseTopup, type TopupInfo } from '../../services/quotaService';
import { toastService } from '../../services/toastService';
import { Logger } from '../../utils/logger';

interface TopupModalProps {
  open: boolean;
  onClose: () => void;
}

function formatCredits(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
  return String(n);
}

/**
 * Modale de recharge de crédits AI (Quota Topup).
 * Affiche le détail de la recharge en crédits et permet l'achat par unités.
 */
export function TopupModal({ open, onClose }: TopupModalProps) {
  const { t } = useLanguage();
  const { refreshQuota } = useQuota();
  const [topupInfo, setTopupInfo] = useState<TopupInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [unitCount, setUnitCount] = useState(1);

  useEffect(() => {
    if (open) {
      setPurchaseSuccess(false);
      setUnitCount(1);
      setIsLoading(true);
      getTopupInfo()
        .then(setTopupInfo)
        .catch((err) => {
          Logger.error('[TopupModal] Failed to load topup info', err);
          toastService.error(
            t.quota?.topupErrorTitle ?? 'Erreur',
            t.quota?.topupLoadError ?? 'Impossible de charger les informations de recharge.'
          );
        })
        .finally(() => setIsLoading(false));
    }
  }, [open, t]);

  const handlePurchase = async () => {
    setIsPurchasing(true);
    try {
      const result = await purchaseTopup(unitCount);
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      setPurchaseSuccess(true);
      await refreshQuota();
      toastService.success(
        t.quota?.topupSuccessTitle ?? 'Recharge effectuée',
        t.quota?.topupSuccessMessage ?? 'Vos crédits AI ont été rechargés avec succès !'
      );
    } catch (err) {
      Logger.error('[TopupModal] Purchase failed', err);
      toastService.error(
        t.quota?.topupErrorTitle ?? 'Erreur',
        t.quota?.topupPurchaseError ?? 'Échec de la recharge. Veuillez réessayer.'
      );
    } finally {
      setIsPurchasing(false);
    }
  };

  const topupT = t.quota ?? {};
  const creditsPerUnit = topupInfo?.topup_credits_per_unit ?? 0;
  const pricePerUnitCents = topupInfo?.topup_price_cents ?? 0;
  const totalCredits = creditsPerUnit * unitCount;
  const totalPriceCents = pricePerUnitCents * unitCount;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-md backdrop-blur-sm"
        style={{ backgroundColor: 'rgba(var(--color-background-rgb, 255, 255, 255), 0.95)' }}
      >
        <DialogTitle className="sr-only">
          {topupT.topupTitle ?? 'Recharger vos crédits AI'}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {topupT.topupDescription ?? 'Achetez une recharge pour débloquer vos crédits AI.'}
        </DialogDescription>

        <div className="flex flex-col items-center gap-4 py-6 px-6">
          {/* Icon */}
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(var(--color-primary-rgb, 85, 0, 255), 0.1)' }}
          >
            {purchaseSuccess ? (
              <Check className="w-7 h-7" style={{ color: 'var(--color-success)' }} />
            ) : (
              <Zap className="w-7 h-7" style={{ color: 'var(--color-primary)' }} />
            )}
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {purchaseSuccess
              ? (topupT.topupSuccessTitle ?? 'Recharge effectuée !')
              : (topupT.topupTitle ?? 'Recharger vos crédits AI')}
          </h3>

          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center gap-2 py-4">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-text-secondary)' }} />
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {topupT.topupLoading ?? 'Chargement...'}
              </span>
            </div>
          )}

          {/* Topup details */}
          {!isLoading && topupInfo && !purchaseSuccess && (
            <div className="w-full space-y-4">
              {/* Plan info */}
              <p className="text-sm text-center" style={{ color: 'var(--color-text-secondary)' }}>
                {topupT.topupPlanInfo ?? 'Rechargez vos crédits AI'}{' '}
                <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  {topupInfo.plan_display_name}
                </span>
              </p>

              {/* Unit count selector */}
              <div
                className="rounded-lg p-4 space-y-3"
                style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    {topupT.topupQuantity ?? 'Quantité'}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      data-testid="topup-qty-dec"
                      onClick={() => setUnitCount(Math.max(1, unitCount - 1))}
                      disabled={unitCount <= 1}
                      className="w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-30"
                      style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--color-border)' }}
                    >
                      <Minus className="w-4 h-4" style={{ color: 'var(--color-text-primary)' }} />
                    </button>
                    <span className="text-lg font-bold w-8 text-center" style={{ color: 'var(--color-text-primary)' }}>
                      {unitCount}
                    </span>
                    <button
                      type="button"
                      data-testid="topup-qty-inc"
                      onClick={() => setUnitCount(unitCount + 1)}
                      className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                      style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--color-border)' }}
                    >
                      <Plus className="w-4 h-4" style={{ color: 'var(--color-text-primary)' }} />
                    </button>
                  </div>
                </div>

                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--color-text-secondary)' }}>
                    {topupT.topupCreditsLabel ?? 'Crédits'}
                  </span>
                  <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    +{formatCredits(totalCredits)}
                  </span>
                </div>

                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--color-text-secondary)' }}>
                    {topupT.topupUnitPrice ?? 'Prix unitaire'}
                  </span>
                  <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    {(pricePerUnitCents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€ / {formatCredits(creditsPerUnit)} {topupT.topupCreditsUnit ?? 'crédits'}
                  </span>
                </div>
              </div>

              {/* Purchased credits balance */}
              {topupInfo.purchased_credits_balance > 0 && (
                <p className="text-xs text-center" style={{ color: 'var(--color-text-tertiary)' }}>
                  {topupT.topupCurrentBalance ?? 'Crédits achetés restants :'}{' '}
                  <span className="font-medium">{formatCredits(topupInfo.purchased_credits_balance)}</span>
                </p>
              )}

              {/* Total price */}
              <div className="text-center">
                <span
                  className="text-2xl font-bold"
                  style={{ color: 'var(--color-primary)' }}
                >
                  {(totalPriceCents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€
                </span>
                <span className="text-sm ml-1" style={{ color: 'var(--color-text-secondary)' }}>HT</span>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-tertiary)' }}>
                  {topupT.topupOneTime ?? 'Paiement unique — crédits persistants'}
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-tertiary)' }}>
                  {topupT.topupVatAtCheckout ?? 'TVA calculée au paiement'}
                </p>
              </div>
            </div>
          )}

          {/* Success state */}
          {purchaseSuccess && (
            <p className="text-sm text-center" style={{ color: 'var(--color-text-secondary)' }}>
              {topupT.topupSuccessMessage ?? 'Vos crédits AI ont été rechargés avec succès !'}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-center mt-2">
            {purchaseSuccess ? (
              <AnimatedIconButton
                icon={Check}
                label={topupT.topupClose ?? 'Fermer'}
                onClick={onClose}
                variant="default"
              />
            ) : (
              <>
                <AnimatedIconButton
                  icon={X}
                  label={t.common?.cancel ?? 'Annuler'}
                  onClick={onClose}
                  variant="secondary"
                />
                <AnimatedIconButton
                  icon={CreditCard}
                  label={topupT.topupBuyButton ?? 'Recharger'}
                  onClick={handlePurchase}
                  variant="default"
                  disabled={isLoading || isPurchasing || !topupInfo}
                  loading={isPurchasing}
                  testId="topup-purchase"
                />
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
